import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    CircularProgress,
    Alert,
    Stack,
    TextField
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import { useCVFiles } from '../../hooks/useCVFiles';
import { HttpService } from 'core/services/http';
import { useSnackbar } from 'notistack';

const SelectCVFileListWidget = ({ onCVFileSelected, selectedId }) => {
    const { data: cvFiles, isLoading, error, refetch } = useCVFiles();
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();

    const handleSelect = (cvFile) => {
        onCVFileSelected(cvFile);
    };

    const handleTitleClick = (e, cvFile) => {
        e.stopPropagation(); // Prevent selecting the CV file when clicking on title
        setEditingId(cvFile.id);
        setEditingTitle(cvFile.title || cvFile.name || `My CV #${cvFile.id}`);
    };

    const handleTitleChange = (e) => {
        setEditingTitle(e.target.value);
    };

    const handleTitleSave = async (cvFileId) => {
        if (!editingTitle.trim()) {
            enqueueSnackbar('Title cannot be empty', { variant: 'error' });
            return;
        }

        try {
            setSaving(true);
            const service = new HttpService();
            await service.put(`/job/cv/cvfile/${cvFileId}`, { title: editingTitle.trim() });
            
            // Refresh the list to get updated data
            await refetch();
            
            setEditingId(null);
            setEditingTitle('');
            enqueueSnackbar('CV file name updated successfully', { variant: 'success' });
        } catch (err) {
            console.error('Error updating CV file title:', err);
            enqueueSnackbar('Failed to update CV file name. Please try again.', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleTitleCancel = () => {
        setEditingId(null);
        setEditingTitle('');
    };

    const handleTitleKeyDown = (e, cvFileId) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleTitleSave(cvFileId);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleTitleCancel();
        }
    };

    // Focus input when editing starts
    useEffect(() => {
        if (editingId && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editingId]);

    const handleFileUpload = async (file) => {
        if (!file) return;

        // Validate file type
        const validTypes = ['application/pdf', 'application/msword', 
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!validTypes.includes(file.type)) {
            enqueueSnackbar('Please upload a PDF, DOC, or DOCX file', { variant: 'error' });
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            enqueueSnackbar('File size must be less than 10MB', { variant: 'error' });
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', file);

            const service = new HttpService();
            const uploadedCVFile = await service.post('/job/cv/cvfile/', formData, true);

            // Refresh the CV files list
            refetch();

            if (uploadedCVFile?.id) {
                handleSelect(uploadedCVFile);
                enqueueSnackbar('CV uploaded successfully', { variant: 'success' });
            }
        } catch (err) {
            console.error('Error uploading CV file:', err);
            enqueueSnackbar('Failed to upload CV. Please try again.', { variant: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleFileInput = (event) => {
        const file = event.target.files[0];
        if (file) {
            handleFileUpload(file);
        }
        // Reset input
        event.target.value = '';
    };

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileUpload(e.dataTransfer.files[0]);
        }
    }, []);

    const cvFileList = cvFiles || [];

    return (
        <Box>
            {/* Drag and Drop Upload Area */}
            <Paper
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                sx={{
                    p: 4,
                    border: '2px dashed',
                    borderColor: dragActive ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    bgcolor: dragActive ? 'primary.50' : 'grey.50',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    mb: 3,
                    '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'primary.50'
                    }
                }}
            >
                <input
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    id="cv-file-upload"
                    type="file"
                    onChange={handleFileInput}
                    disabled={uploading}
                />
                <label htmlFor="cv-file-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                    <Stack spacing={2} alignItems="center">
                        <CloudUploadIcon 
                            sx={{ 
                                fontSize: 48, 
                                color: dragActive ? 'primary.main' : 'text.secondary' 
                            }} 
                        />
                        <Box>
                            <Typography variant="h6" fontWeight={500} gutterBottom>
                                {uploading ? 'Uploading...' : 'Drag & drop your CV here'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                or click to browse
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Supported formats: PDF, DOC, DOCX (Max 10MB)
                            </Typography>
                        </Box>
                        {uploading && (
                            <CircularProgress size={24} />
                        )}
                    </Stack>
                </label>
            </Paper>

            {/* Existing CV Files List */}
            {cvFileList.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                        Previously Uploaded Files
                    </Typography>
                    <List sx={{ p: 0 }}>
                        {cvFileList.map((cvFile) => {
                            const isSelected = selectedId === cvFile.id;
                            return (
                                <ListItem
                                    key={cvFile.id}
                                    disablePadding
                                    sx={{
                                        mb: 1,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        borderRadius: 1,
                                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: isSelected ? 'primary.50' : 'grey.50'
                                        }
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => {
                                            // Don't select if currently editing this item
                                            if (editingId !== cvFile.id) {
                                                handleSelect(cvFile);
                                            }
                                        }}
                                        sx={{ py: 2, px: 2 }}
                                    >
                                        <DescriptionIcon 
                                            sx={{ 
                                                color: isSelected ? 'primary.main' : 'text.secondary',
                                                mr: 2
                                            }} 
                                        />
                                        <ListItemText
                                            primary={
                                                editingId === cvFile.id ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                        <TextField
                                                            inputRef={inputRef}
                                                            value={editingTitle}
                                                            onChange={handleTitleChange}
                                                            onBlur={() => handleTitleSave(cvFile.id)}
                                                            onKeyDown={(e) => handleTitleKeyDown(e, cvFile.id)}
                                                            disabled={saving}
                                                            size="small"
                                                            variant="standard"
                                                            fullWidth
                                                            sx={{
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '1rem',
                                                                    fontWeight: isSelected ? 600 : 400,
                                                                    color: isSelected ? 'primary.main' : 'text.primary',
                                                                    py: 0.5,
                                                                },
                                                                '& .MuiInput-underline:before': {
                                                                    borderBottomColor: isSelected ? 'primary.main' : 'rgba(0, 0, 0, 0.42)',
                                                                },
                                                                '& .MuiInput-underline:hover:before': {
                                                                    borderBottomColor: 'primary.main',
                                                                },
                                                            }}
                                                        />
                                                        {saving && (
                                                            <CircularProgress size={16} />
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            cursor: 'pointer',
                                                            '&:hover .edit-icon': {
                                                                opacity: 1,
                                                            }
                                                        }}
                                                        onClick={(e) => handleTitleClick(e, cvFile)}
                                                    >
                                                        <Typography 
                                                            variant="subtitle1" 
                                                            fontWeight={isSelected ? 600 : 400}
                                                            color={isSelected ? 'primary.main' : 'text.primary'}
                                                        >
                                                            {cvFile.title || cvFile.name || `My CV #${cvFile.id}`}
                                                        </Typography>
                                                        <EditIcon 
                                                            className="edit-icon"
                                                            sx={{ 
                                                                fontSize: 16,
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s',
                                                                color: 'text.secondary',
                                                                '&:hover': {
                                                                    color: 'primary.main',
                                                                }
                                                            }} 
                                                        />
                                                    </Box>
                                                )
                                            }
                                            secondary={
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    {cvFile.created_at 
                                                        ? `Uploaded: ${new Date(cvFile.created_at).toLocaleDateString()}`
                                                        : 'CV File'
                                                    }
                                                </Typography>
                                            }
                                        />
                                        {isSelected && (
                                            <CheckCircleIcon 
                                                sx={{ 
                                                    color: 'primary.main',
                                                    ml: 2,
                                                    fontSize: 28
                                                }} 
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            )}

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    Failed to load CV files. Please try again.
                </Alert>
            )}
        </Box>
    );
};

export default SelectCVFileListWidget;
